import {
  inject,
  signal,
  Component,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { faKey } from '@fortawesome/free-solid-svg-icons';

import { SuiDialogBase, DialogCloseReason } from '@sui/dialog';
import { User } from 'src/app/core/models/core.models';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';

import { injectChangePasswordMutation } from 'src/app/api/queries/user.queries';

import { AppState } from 'src/app/reducers';
import { selectUser } from 'src/app/core/reducers';

import { tap } from 'rxjs';

interface DialogData {
  user: User | null;
}
interface DialogOutput {}

@Component({
  selector: 'app-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styleUrl: './change-password-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChangePasswordDialogComponent extends SuiDialogBase<
  DialogData,
  DialogOutput
> {
  store = inject(Store<AppState>);
  destroyRef = inject(DestroyRef);
  formService = inject(FormsService);
  translateService = inject(TranslateService);

  private currentUserMode = false;

  private userId: string;

  formGroup: FormGroup;
  objectIcon = faKey;
  objectTitle = this.translateService.instant(
    'change-password.title.change-password',
  );

  isValid = signal<boolean>(false);

  confirmDisabled = signal<boolean>(false);
  confirmLoading = signal<boolean>(false);

  public formProperties: FormProperty[] = [];

  protected cdr = inject(ChangeDetectorRef);

  private readonly changePasswordMutation = injectChangePasswordMutation();

  ngOnInit(): void {
    if (this.dialogData.user) {
      this.userId = this.dialogData.user.id;
      this.currentUserMode = false; // changing someone else
      this.initForm();
    } else {
      this.store
        .select(selectUser)
        .pipe(
          tap((user: User) => {
            this.userId = user.id;
            this.currentUserMode = true;
            this.initForm();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  private initForm(): void {
    // Build fields based on mode
    this.formProperties =
      this.currentUserMode === false
        ? [
            {
              propertyName: 'new_password',
              propertyLabel: this.translateService.instant(
                'change-password-popup.label.new-password',
              ),
              controlType: 'password',
              validators: [Validators.required],
              additionalConfig: { placeholder: '', strength: true },
            },
          ]
        : [
            {
              propertyName: 'current_password',
              propertyLabel: this.translateService.instant(
                'change-password-popup.label.current-password',
              ),
              controlType: 'password',
              validators: [Validators.required],
            },
            {
              propertyName: 'new_password',
              propertyLabel: this.translateService.instant(
                'change-password-popup.label.new-password',
              ),
              controlType: 'password',
              validators: [Validators.required],
              additionalConfig: { placeholder: '', strength: true },
            },
            {
              propertyName: 'confirm_password',
              propertyLabel: this.translateService.instant(
                'change-password-popup.label.confirm-password',
              ),
              controlType: 'password',
              validators: [
                Validators.required,
                this.passwordMatchValidator('new_password'),
              ],
            },
          ];

    this.formGroup = this.formService.createFormGroup(this.formProperties);
    this.formGroup.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => this.isValid.set(status === 'VALID'));
    // dynamic for do not appear after init without this line

    queueMicrotask(() => this.cdr.markForCheck());
  }

  ngAfterViewInit(): void {}

  getFormData() {
    return {
      currentPassword: this.formGroup.get('current_password')?.value ?? null,
      newPassword: this.formGroup.get('new_password')?.value ?? null,
      confirmPassword: this.formGroup.get('confirm_password')?.value ?? null,
    };
  }

  passwordMatchValidator(passwordField: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const password = control.root.get(passwordField);
      if (password && control.value !== password.value) {
        return { passwordMismatch: true };
      }
      return null;
    };
  }

  save(): void {
    if (!this.isValid()) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const { currentPassword, newPassword, confirmPassword } =
      this.getFormData();

    this.confirmLoading.set(true);
    this.confirmDisabled.set(true);

    // Per-call callbacks on purpose: closing the dialog and releasing the confirm
    // button only matter while it is still open.
    this.changePasswordMutation.mutate(
      {
        userId: this.userId,
        currentPassword,
        newPassword,
        confirmPassword,
      },
      {
        onSuccess: () =>
          this.close({
            reason: DialogCloseReason.Save,
            data: this.getFormData(),
          }),
        onError: () => {
          this.confirmLoading.set(false);
          this.confirmDisabled.set(false);
        },
      },
    );
  }

  cancel(): void {
    this.close({
      reason: DialogCloseReason.Cancel,
    });
  }
}
